import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Search, Trash2, CheckCircle, Reply, ChevronDown, ChevronUp, User, Clock, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { useTranslation } from 'react-i18next';
import adminApi from '../../services/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import useAdminStore from '../../store/adminStore';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const ContactMessagesTable = () => {
  const { t, i18n } = useTranslation('admin');
  const dateLocale = getDateFnsLocale(i18n.language);
  const { authToken } = useAuth();
  const { setLoading, setError, error, loading } = useAdminStore();
  const [messages, setMessages] = useState([]);
  const [expandedMessage, setExpandedMessage] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  });

  const loadMessages = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.getContactMessages({ token: authToken });
      if (result.success) {
        setMessages(result.messages || []);
      } else {
        setError(result.error || t('contactMessagesTable.loadFailedFallback'));
      }
    } catch (error) {
      console.error('Mesaj yükleme hatası:', error);
      setError(getApiErrorMessage(error, { fallback: t('contactMessagesTable.genericErrorFallback') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [authToken]);

  const handleUpdateStatus = async (messageId, newStatus) => {
    try {
      const result = await adminApi.updateContactMessageStatus({
        token: authToken,
        messageId,
        status: newStatus
      });
      if (result.success) {
        setMessages(prev => prev.map(m => 
          m.id === messageId ? { ...m, status: newStatus } : m
        ));
      }
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
    }
  };

  const filteredMessages = messages.filter(m => {
    const matchesSearch = 
      m.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      m.email.toLowerCase().includes(filters.search.toLowerCase()) ||
      m.subject.toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus = filters.status === 'all' || m.status === filters.status;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd MMM yyyy, HH:mm', { locale: dateLocale });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      unread: { label: t('contactMessagesTable.statusBadge.unread'), classes: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
      read: { label: t('contactMessagesTable.statusBadge.read'), classes: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      replied: { label: t('contactMessagesTable.statusBadge.replied'), classes: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' }
    };
    const config = configs[status] || configs.unread;
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${config.classes}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white">{t('contactMessagesTable.title')}</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('contactMessagesTable.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={t('contactMessagesTable.searchPlaceholder')}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="pl-9 pr-4 py-2 bg-white dark:bg-[var(--color-bg-elevated)] border border-slate-200 dark:border-white/10 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none w-full sm:w-64"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-3 py-2 bg-white dark:bg-[var(--color-bg-elevated)] border border-slate-200 dark:border-white/10 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="all">{t('contactMessagesTable.statusFilter.all')}</option>
            <option value="unread">{t('contactMessagesTable.statusFilter.unread')}</option>
            <option value="read">{t('contactMessagesTable.statusFilter.read')}</option>
            <option value="replied">{t('contactMessagesTable.statusFilter.replied')}</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-xl text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-[var(--color-bg-elevated)] rounded-2xl border border-slate-200 dark:border-white/10 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 dark:bg-white/5 border-b border-slate-200 dark:border-white/10 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                <th className="px-6 py-4">{t('contactMessagesTable.columns.sender')}</th>
                <th className="px-6 py-4">{t('contactMessagesTable.columns.subject')}</th>
                <th className="px-6 py-4">{t('contactMessagesTable.columns.date')}</th>
                <th className="px-6 py-4">{t('contactMessagesTable.columns.status')}</th>
                <th className="px-6 py-4 text-right">{t('contactMessagesTable.columns.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {loading && messages.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-500">{t('contactMessagesTable.loading')}</td>
                </tr>
              ) : filteredMessages.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-500">{t('contactMessagesTable.noMessagesFound')}</td>
                </tr>
              ) : (
                filteredMessages.map((msg) => (
                  <React.Fragment key={msg.id}>
                    <tr 
                      className={`
                        hover:bg-slate-50 dark:hover:bg-white/5 transition-colors cursor-pointer
                        ${expandedMessage === msg.id ? 'bg-slate-50 dark:bg-white/5' : ''}
                        ${msg.status === 'unread' ? 'font-medium' : ''}
                      `}
                      onClick={() => setExpandedMessage(expandedMessage === msg.id ? null : msg.id)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="size-8 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                            <User className="size-4" />
                          </div>
                          <div>
                            <div className="text-sm text-slate-900 dark:text-white">{msg.name}</div>
                            <div className="text-xs text-slate-500">{msg.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{msg.subject}</td>
                      <td className="px-6 py-4 text-sm text-slate-500 whitespace-nowrap">{formatDate(msg.created_at)}</td>
                      <td className="px-6 py-4">{getStatusBadge(msg.status)}</td>
                      <td className="px-6 py-4 text-right">
                        <button className="p-2 text-slate-400 hover:text-blue-600 transition-colors">
                          {expandedMessage === msg.id ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}
                        </button>
                      </td>
                    </tr>
                    <AnimatePresence>
                      {expandedMessage === msg.id && (
                        <tr>
                          <td colSpan="5" className="px-0 py-0 border-none!">
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden bg-slate-50/50 dark:bg-white/[0.02]"
                            >
                              <div className="px-14 py-6 space-y-4">
                                <div className="space-y-2">
                                  <h5 className="text-xs font-bold uppercase text-slate-400 tracking-wider">{t('contactMessagesTable.messageContent')}</h5>
                                  <div className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed bg-white dark:bg-slate-900/50 p-4 rounded-xl border border-slate-200 dark:border-white/10">
                                    {msg.message}
                                  </div>
                                </div>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4 text-xs text-slate-500">
                                    <span className="flex items-center gap-1"><Clock className="size-3" /> {formatDate(msg.created_at)}</span>
                                    <span className="flex items-center gap-1"><MessageSquare className="size-3" /> ID: {msg.id.substring(0, 8)}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {msg.status === 'unread' && (
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); handleUpdateStatus(msg.id, 'read'); }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 transition-all"
                                      >
                                        <CheckCircle className="size-3.5" /> {t('contactMessagesTable.markAsRead')}
                                      </button>
                                    )}
                                    {msg.status !== 'replied' && (
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); handleUpdateStatus(msg.id, 'replied'); }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400 transition-all"
                                      >
                                        <Reply className="size-3.5" /> {t('contactMessagesTable.markAsReplied')}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          </td>
                        </tr>
                      )}
                    </AnimatePresence>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ContactMessagesTable;
